import { SWRFetcher } from '@commenda/utils';
import useSWR, { type SWRConfiguration } from 'swr';

import {
  TIndirectTaxTransactionExtend,
  TIndirectTaxTransactionListResponse,
  TIndirectTaxListTransactionsParams,
} from './types';
import { IndirectTaxTransactionQueryUtils } from './transaction-query-utils';

// Transaction list reads are DB-heavy; focus revalidation can replay the same
// expensive search repeatedly. Use bounded visible-tab refresh instead.
const TRANSACTION_LIST_SWR_OPTIONS: SWRConfiguration = {
  keepPreviousData: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  refreshWhenHidden: false,
  dedupingInterval: 30_000,
  refreshInterval: 60_000,
};

export function useIndirectTaxTransactions({
  token,
  filters,
  cursor = null,
  limit = 100,
  showTaxBreakdown = false,
  sortState,
}: TIndirectTaxListTransactionsParams) {
  const query = IndirectTaxTransactionQueryUtils.buildQueryParams(filters, sortState);

  if (cursor) query.append('cursor', cursor);
  query.append('limit', limit.toString());

  if (showTaxBreakdown) {
    query.append('extend', `${TIndirectTaxTransactionExtend.SHIPPING_ADDRESS},${TIndirectTaxTransactionExtend.SHIP_FROM_ADDRESS}`);
  } else {
    query.append(
      'extend',
      `${TIndirectTaxTransactionExtend.SHIPPING_ADDRESS},${TIndirectTaxTransactionExtend.CUSTOMER},${TIndirectTaxTransactionExtend.SHIP_FROM_ADDRESS}`
    );
  }

  const { data, error, isLoading, mutate } =
    useSWR<TIndirectTaxTransactionListResponse>(
      `new-sales-tax/transactions?${query}`,
      SWRFetcher({ method: 'GET' }, token),
      TRANSACTION_LIST_SWR_OPTIONS
    );

  return {
    data: data
      ? {
          transactions: data.data,
          nextCursor: data.nextCursor,
          totalTransactions: data.totalTransactions,
        }
      : {
          transactions: [],
          nextCursor: null,
          totalTransactions: 0,
        },
    error,
    mutate,
    isLoading,
  };
}
